import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { AppModal } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import { withdrawPatientSsnApi } from "@/portals/patient/api/patientProfileApi";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { SsnModal } from "./SsnModal";
import { SSN_COPY } from "./ssnConsentCopy";

function formatDate(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

/**
 * The real, backend-backed SSN consent on the Consent & Privacy page. The
 * other cards on that page still run on mock data; this one reads the
 * live profile and calls the API, so "on" means a hash is actually stored
 * and "Remove" actually deletes it.
 */
export function SsnConsentCard() {
  const { profile, setProfile } = usePatientProfile();
  const [addOpen, setAddOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const isOn = Boolean(profile.ssnProvidedAt);

  const handleWithdraw = async () => {
    setWithdrawing(true);
    const { error } = await withdrawPatientSsnApi();
    setWithdrawing(false);
    if (error) {
      toastError(error);
      return;
    }
    setProfile({ ...profile, ssnProvidedAt: null });
    setConfirmOpen(false);
    toastSuccess(SSN_COPY.withdrawn);
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-l-4 border-[#E5E7EB] bg-white p-5 shadow-sm ${
        isOn ? "border-l-[#F36F8F]" : "border-l-[#111827]"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${isOn ? "bg-[#F36F8F]" : "bg-[#6B7280]"}`}>
            <Icon icon="lucide:fingerprint" className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-base font-extrabold text-[#0A2947]">{SSN_COPY.title}</h3>
        </div>
        <span className={`text-xs font-semibold ${isOn ? "text-[#E4002B]" : "text-[#9CA3AF]"}`}>
          {isOn ? "Active" : "Not provided"}
        </span>
      </div>

      <p className="mt-3 text-sm text-[#4B5563]">{SSN_COPY.consentCardDescription}</p>
      <p className="mt-2 text-sm text-[#4B5563]">{SSN_COPY.notRequired}</p>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button variant="outline" onClick={() => setAddOpen(true)} className="!h-auto !rounded-full !px-4 !py-2 !text-xs !font-semibold">
          {isOn ? "Update SSN" : "Add SSN"}
        </Button>
        {isOn && (
          <Button variant="outline" onClick={() => setConfirmOpen(true)} className="!h-auto !rounded-full !border-[#F36F8F] !px-4 !py-2 !text-xs !font-semibold !text-[#E4002B]">
            {SSN_COPY.withdraw}
          </Button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-[#E5E7EB] pt-3 text-xs text-[#9CA3AF]">
        <span className="flex items-center gap-1.5">
          <Icon icon="lucide:clock" className="h-3.5 w-3.5" />
          {isOn ? (
            <>Provided <span className="font-semibold text-[#111827]">{formatDate(profile.ssnProvidedAt!)}</span></>
          ) : (
            "Never provided"
          )}
        </span>
        <Link
          to="/privacy-policy"
          state={{ from: "/patient/consent-privacy" }}
          className="flex items-center gap-1 font-semibold text-[#E4002B] hover:underline"
        >
          How we protect your SSN
          <Icon icon="lucide:external-link" className="h-3 w-3" />
        </Link>
      </div>

      <SsnModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={(ssnProvidedAt) => {
          setProfile({ ...profile, ssnProvidedAt });
          toastSuccess("SSN saved.");
        }}
      />

      <AppModal
        open={confirmOpen}
        onOpenChange={(open) => { if (!open && !withdrawing) setConfirmOpen(false); }}
        width={384}
        className="[&_.ant-modal-content]:!rounded-3xl"
      >
        <h2 className="text-base font-extrabold text-[#180047]">{SSN_COPY.withdrawConfirmTitle}</h2>
        <p className="mt-1 text-sm text-[#6B7280]">{SSN_COPY.withdrawConfirmBody}</p>
        <div className="mt-6 flex items-center gap-3">
          <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={withdrawing} className="!h-auto !rounded-full !px-5 !py-2.5 !text-sm !font-semibold">
            Cancel
          </Button>
          <Button onClick={handleWithdraw} busy={withdrawing} className="!h-auto !rounded-full !bg-[#F36F8F] !px-5 !py-2.5 !text-sm !font-semibold !text-white">
            {SSN_COPY.withdrawConfirm}
          </Button>
        </div>
      </AppModal>
    </div>
  );
}
